import {
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
} from 'react';
import { Streamdown } from 'streamdown';
import type { RenderEmailRequest } from '@/api/email-builder/email-builder.types.ts';
import type { EmailBuilderUIMessage } from '@/api/ai/ai.types';
import { useEmailBuilderChat } from '../hooks/use-email-builder-chat';
import 'streamdown/styles.css';

interface AiChatPaneProps {
  /** Live builder draft — re-sent to the agent as context on every turn. */
  draft: RenderEmailRequest;
  /** Applies the AI's resolved edits to the builder state. */
  onApplyEdits: (draft: RenderEmailRequest) => void;
}

/** One-click starter prompts shown in the empty state. */
const AI_SUGGESTIONS = [
  {
    label: 'Produktankündigung',
    prompt: 'Erstelle eine E-Mail für eine neue Produktankündigung.',
  },
  {
    label: 'Willkommens-Mail',
    prompt: 'Schreibe eine Willkommens-E-Mail für neue Geschäftspartner.',
  },
  {
    label: 'Rabatt-Aktion',
    prompt: 'Baue eine E-Mail für eine zeitlich begrenzte Rabatt-Aktion.',
  },
  {
    label: 'Betreff verbessern',
    prompt: 'Schlage drei stärkere Betreffzeilen für meine E-Mail vor.',
  },
] as const;

/** Geometric agent sigil — reused as the chat avatar and the KI tab icon. */
export function AgentMark({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 16 16" aria-hidden="true">
      <path
        d="M8 1.4 14.6 8 8 14.6 1.4 8Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
      <circle cx="8" cy="8" r="2.5" fill="currentColor" />
    </svg>
  );
}

type MessagePart = EmailBuilderUIMessage['parts'][number];
type ToneState = 'running' | 'done' | 'error';

/** Animated three-dot "thinking" bubble shown before the first token. */
function ThinkingBubble() {
  return (
    <article className="eb-msg is-assistant">
      <span className="eb-msg-avatar" aria-hidden="true">
        <AgentMark />
      </span>
      <div className="eb-msg-main">
        <div className="eb-msg-bubble eb-think" aria-live="polite">
          <span className="eb-think-dots" aria-hidden="true">
            <span className="eb-think-dot" />
            <span className="eb-think-dot" />
            <span className="eb-think-dot" />
          </span>
          <span className="eb-think-label">Der Assistent denkt nach…</span>
        </div>
      </div>
    </article>
  );
}

/** Compact activity card for a tool the agent invoked. */
function ToolActivity({
  tone,
  title,
  detail,
}: {
  tone: ToneState;
  title: string;
  detail?: string;
}) {
  return (
    <div className={`eb-tool is-${tone}`}>
      <span className="eb-tool-icon" aria-hidden="true">
        {tone === 'running' ? (
          <span className="eb-think-dots">
            <span className="eb-think-dot" />
            <span className="eb-think-dot" />
            <span className="eb-think-dot" />
          </span>
        ) : tone === 'error' ? (
          '!'
        ) : (
          '✓'
        )}
      </span>
      <div className="eb-tool-body">
        <span className="eb-tool-title">{title}</span>
        {detail && <span className="eb-tool-detail">{detail}</span>}
      </div>
    </div>
  );
}

/** Renders the `applyEmailEdits` tool call as a draft-edit activity card. */
function EditActivity({
  part,
}: {
  part: Extract<MessagePart, { type: 'tool-applyEmailEdits' }>;
}) {
  const summary = part.input?.summary;
  if (part.state === 'output-error') {
    return (
      <ToolActivity
        tone="error"
        title="Änderung fehlgeschlagen"
        detail={part.errorText}
      />
    );
  }
  if (part.state === 'output-available') {
    const warnings = part.output.warnings;
    return (
      <ToolActivity
        tone="done"
        title="Editor aktualisiert"
        detail={
          warnings && warnings.length > 0 ? warnings.join(' ') : summary
        }
      />
    );
  }
  return (
    <ToolActivity tone="running" title="Editor wird angepasst…" detail={summary} />
  );
}

/** Renders a backend catalog lookup (`searchItems` / `getItem`) as a chip. */
function CatalogActivity({
  part,
}: {
  part: Extract<MessagePart, { type: 'tool-searchItems' | 'tool-getItem' }>;
}) {
  const running =
    part.state === 'input-streaming' || part.state === 'input-available';
  const failed = part.state === 'output-error';
  const noun = part.type === 'tool-searchItems' ? 'Katalog' : 'Artikel';
  const query =
    part.type === 'tool-searchItems'
      ? part.input?.query
      : part.input?.itemId;
  const title = running
    ? `${noun} wird durchsucht…`
    : failed
      ? `${noun}suche fehlgeschlagen`
      : `${noun} durchsucht`;
  return (
    <ToolActivity
      tone={running ? 'running' : failed ? 'error' : 'done'}
      title={title}
      detail={query ? `„${query}“` : undefined}
    />
  );
}

/** Renders a single message part, or `null` for parts with no chat UI. */
function PartView({ part, isUser }: { part: MessagePart; isUser: boolean }) {
  switch (part.type) {
    case 'text':
      if (!part.text.trim()) return null;
      // User input stays literal; assistant replies are streamed markdown.
      return isUser ? (
        <div className="eb-msg-bubble">{part.text}</div>
      ) : (
        <div className="eb-msg-bubble eb-md">
          <Streamdown controls={false}>{part.text}</Streamdown>
        </div>
      );
    case 'tool-applyEmailEdits':
      return <EditActivity part={part} />;
    case 'tool-searchItems':
    case 'tool-getItem':
      return <CatalogActivity part={part} />;
    case 'dynamic-tool':
      return (
        <ToolActivity
          tone={part.state === 'output-available' ? 'done' : 'running'}
          title={`Werkzeug: ${part.toolName}`}
        />
      );
    default:
      return null;
  }
}

/** True when a message has at least one part the chat actually renders. */
function hasRenderablePart(message: EmailBuilderUIMessage): boolean {
  return message.parts.some(
    (part) =>
      (part.type === 'text' && part.text.trim().length > 0) ||
      part.type === 'tool-applyEmailEdits' ||
      part.type === 'tool-searchItems' ||
      part.type === 'tool-getItem' ||
      part.type === 'dynamic-tool',
  );
}

function ChatMessage({ message }: { message: EmailBuilderUIMessage }) {
  const isUser = message.role === 'user';
  return (
    <article className={`eb-msg ${isUser ? 'is-user' : 'is-assistant'}`}>
      {!isUser && (
        <span className="eb-msg-avatar" aria-hidden="true">
          <AgentMark />
        </span>
      )}
      <div className="eb-msg-main">
        <div className="eb-msg-meta">
          <span className="eb-msg-who">{isUser ? 'Du' : 'EOS Assistent'}</span>
        </div>
        {message.parts.map((part, index) => (
          <PartView key={`${message.id}-${index}`} part={part} isUser={isUser} />
        ))}
      </div>
    </article>
  );
}

interface ComposerProps {
  value: string;
  busy: boolean;
  onChange: (value: string) => void;
  onSubmit: () => void;
  onStop: () => void;
}

function Composer({ value, busy, onChange, onSubmit, onStop }: ComposerProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Auto-grow the textarea up to a capped height.
  useLayoutEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    textarea.style.height = 'auto';
    textarea.style.height = `${Math.min(textarea.scrollHeight, 132)}px`;
  }, [value]);

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      if (!busy) onSubmit();
    }
  };

  return (
    <div className="eb-ai-composer">
      <textarea
        ref={textareaRef}
        className="eb-ai-input"
        rows={1}
        value={value}
        placeholder="Beschreibe deine E-Mail oder stelle eine Frage…"
        aria-label="Nachricht an den Assistenten"
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={handleKeyDown}
      />
      {busy ? (
        <button
          type="button"
          className="eb-ai-send eb-ai-stop"
          aria-label="Generierung stoppen"
          onClick={onStop}
        >
          <svg viewBox="0 0 16 16" aria-hidden="true">
            <rect x="4.5" y="4.5" width="7" height="7" rx="1" fill="currentColor" />
          </svg>
        </button>
      ) : (
        <button
          type="button"
          className="eb-ai-send"
          aria-label="Senden"
          disabled={!value.trim()}
          onClick={onSubmit}
        >
          <svg viewBox="0 0 16 16" aria-hidden="true">
            <path
              d="M8 13.2V3.4M8 3.4 3.6 7.8M8 3.4l4.4 4.4"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.7"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      )}
    </div>
  );
}

/**
 * Left-pane AI assistant — a live streaming chat that helps the user build the
 * email. The agent edits the draft directly through the `applyEmailEdits` tool;
 * the pane is kept mounted (toggled via the view tabs) so the conversation
 * survives switching back to the block editor.
 */
export function AiChatPane({ draft, onApplyEdits }: AiChatPaneProps) {
  const { messages, status, error, isBusy, send, stop, retry, reset } =
    useEmailBuilderChat({ draft, onApplyEdits });
  const [composerValue, setComposerValue] = useState('');
  const threadRef = useRef<HTMLDivElement>(null);

  // Keep the newest message in view as the conversation streams.
  useLayoutEffect(() => {
    const thread = threadRef.current;
    if (thread) thread.scrollTop = thread.scrollHeight;
  }, [messages, status]);

  const hasConversation = messages.length > 0;

  const submitComposer = () => {
    const text = composerValue.trim();
    if (!text || isBusy) return;
    send(text);
    setComposerValue('');
  };

  const sendPrompt = (prompt: string) => {
    if (isBusy) return;
    send(prompt);
  };

  return (
    <div className="eb-ai">
      <header className="eb-ai-head">
        <span className="eb-ai-mark">
          <AgentMark />
        </span>
        <span className="eb-ai-id">
          <span className="eb-ai-name">EOS Assistent</span>
          <span className="eb-ai-role">E-Mail-Copilot</span>
        </span>
        {hasConversation && (
          <button
            type="button"
            className="eb-ai-reset"
            onClick={() => {
              reset();
              setComposerValue('');
            }}
          >
            Neuer Chat
          </button>
        )}
      </header>

      <div
        className="eb-ai-thread ds-scroll"
        ref={threadRef}
        role="log"
        aria-live="polite"
        aria-label="Konversation mit dem Assistenten"
      >
        {!hasConversation && (
          <div className="eb-ai-empty">
            <span className="eb-ai-empty-mark">
              <AgentMark />
            </span>
            <p className="eb-ai-empty-text">
              Ich baue deine E-Mail direkt im Editor auf — beschreibe das Ziel,
              und ich lege Struktur, Texte und Produkte an. Du kannst jederzeit
              im Editor nachjustieren.
            </p>
          </div>
        )}

        {messages
          .filter(hasRenderablePart)
          .map((message) => (
            <ChatMessage key={message.id} message={message} />
          ))}

        {status === 'submitted' && <ThinkingBubble />}

        {status === 'error' && (
          <div className="eb-ai-error" role="alert">
            <span className="eb-ai-error-text">
              {error?.message ?? 'Der Assistent ist nicht erreichbar.'}
            </span>
            <button type="button" className="eb-ai-reset" onClick={retry}>
              Erneut versuchen
            </button>
          </div>
        )}
      </div>

      <div className="eb-ai-foot">
        {!hasConversation && (
          <div className="eb-ai-suggest">
            {AI_SUGGESTIONS.map((suggestion, index) => (
              <button
                key={suggestion.label}
                type="button"
                className="eb-ai-suggest-card"
                style={{ '--i': index } as CSSProperties}
                disabled={isBusy}
                onClick={() => sendPrompt(suggestion.prompt)}
              >
                <span className="eb-ai-suggest-label">{suggestion.label}</span>
                <span className="eb-ai-suggest-hint">{suggestion.prompt}</span>
              </button>
            ))}
          </div>
        )}
        <Composer
          value={composerValue}
          busy={isBusy}
          onChange={setComposerValue}
          onSubmit={submitComposer}
          onStop={stop}
        />
      </div>
    </div>
  );
}
